import css from '@styled-system/css';
import variant from '@styled-system/variant';
import { ThemeStyles } from '@honda-canada/design-system-react';
import { TIMELINE_TEXT_ALIGNMENT, TIMELINE_COLOR } from './constants';

const themeStyles = new ThemeStyles();

const centerTextColor = variant({
  prop: 'textContentAlignment',
  variants: {
    [TIMELINE_TEXT_ALIGNMENT.CENTER]: {
      color: 'white',
      textAlign: 'center',
    },
  },
});

themeStyles.addBaseStyles('ItemContainer')(
  variant({
    prop: 'textContentAlignment',
    variants: {
      [TIMELINE_TEXT_ALIGNMENT.LEFT]: {
        flexDirection: ['column', 'row'],
      },
      [TIMELINE_TEXT_ALIGNMENT.CENTER]: {
        textAlign: 'center',
        flexDirection: ['column', 'row'],
      },
      [TIMELINE_TEXT_ALIGNMENT.RIGHT]: {
        flexDirection: ['column-reverse', 'row-reverse'],
      },
    },
  }),
  variant({
    prop: 'bgColor',
    variants: {
      [TIMELINE_COLOR.BLUE]: {
        backgroundColor: '#DBEDFF',
      },
      [TIMELINE_COLOR.GREEN]: {
        backgroundColor: '#E4F0D6',
      },
      [TIMELINE_COLOR.GREY]: {
        backgroundColor: 'grey.4',
      },
      [TIMELINE_COLOR.ORANGE]: {
        backgroundColor: '#FDEEDB',
      },
      [TIMELINE_COLOR.WHITE]: {
        backgroundColor: 'white',
      },
      [TIMELINE_COLOR.YELLOW]: {
        backgroundColor: '#F0EED8',
      },
    },
  }),
  ({ containerImage, isVisible }) =>
    css({
      width: '100%',
      // TODO: 90px is the height of the header + accordion header,
      // 114px is the height of the header + decade navigation
      // this should be replaced with a more robust solution
      // that doesn't rely on hardcoded values
      minHeight: ['calc(100vh - 90px)', 'calc(100vh - 114px)'],
      display: 'flex',
      justifyContent: 'center',
      opacity: isVisible ? '1' : '0.05',
      transition: 'opacity 0.1s ease',
      alignItems: ['start', 'center'],
      gap: ['16px', '64px'],
      padding: ['0', '50px 96px'],
      paddingBottom: ['20px', 0],
      position: 'relative',
      scrollSnapAlign: 'start',
      scrollMarginTop: ['90px', '114px'],
      scrollSnapStop: 'always',
      // Fix ME z-index to prevent other elements showing on it when accordion is opened
      zIndex: 180,
      ...(containerImage
        ? {
            backgroundImage: `url(${containerImage})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            justifyContent: 'center',
            alignItems: 'center',
          }
        : {}),
    }),
);

themeStyles.addBaseStyles('TimelineNavigationLink')(
  ({ isActive }) =>
    css({
      fontWeight: 'bold',
      minHeight: '44px',
      color: isActive ? 'red' : 'black',
      boxSizing: 'border-box',
      transition: 'border 200ms ease-out',
      '&:hover': {
        color: 'red',
        border: 'none',
      },
    }),
  ({ isActive, theme }) =>
    variant({
      prop: 'navSection',
      variants: {
        side: {
          borderLeft: theme.borders[3],
          borderLeftColor: isActive ? 'red' : 'grey.2',
          padding: '24px',
          '&:hover, &:focus, &:active': {
            border: theme.borders[0],
            borderLeft: theme.borders[3],
            borderLeftColor: isActive ? 'red' : 'grey.2',
          },
        },
        top: {
          borderBottom: theme.borders[3],
          paddingTop: '5px',
          paddingBottom: '5px',
          borderBottomColor: isActive ? 'red' : 'transparent',
          '&:hover, &:focus, &:active': {
            borderBottom: isActive ? theme.borders[3] : theme.borders[0],
            borderBottomColor: isActive ? 'red' : 'transparent',
          },
        },
      },
    }),
);

themeStyles.addBaseStyles('Content')(
  {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    maxWidth: '540px',
  },

  css({
    justifyContent: ['initial', 'center'],
    paddingLeft: ['16px', 0],
    paddingRight: ['16px', 0],
  }),
);

themeStyles.addBaseStyles('ContentTitle')(
  {
    textTransform: 'none',
    margin: 0,
    lineHeight: ['32px', '50px'],
    fontSize: ['28px', '44px'],
  },
  centerTextColor,
);

themeStyles.addBaseStyles('ContentSubTitle')(
  css({
    textTransform: 'none',
    margin: 0,
    lineHeight: ['36px', '64px'],
    fontSize: ['32px', '56px'],
  }),
  centerTextColor,
);

themeStyles.addBaseStyles('ContentCategory')(
  css({
    color: 'darkRed',
    fontWeight: 'bold',
    lineHeight: ['1.5em', '1.6875em'],
    fontSize: ['1em', '1.125em'],
  }),
  centerTextColor,
);

themeStyles.addBaseStyles('ContentBody')(
  css({
    lineHeight: ['1.3125em', '1.5em'],
    fontSize: ['0.875em', '1em'],
  }),
  centerTextColor,
);

themeStyles.addBaseStyles('MediaContainer')(
  css({
    maxWidth: '100%',
    margin: ['0 auto', 0],
  }),
);

themeStyles.addBaseStyles('CTALink')(
  css({
    width: 'fit-content',
    display: 'flex !important',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '18px',
    '&:hover > div': {
      borderColor: 'transparent transparent transparent black',
    },
  }),
  variant({
    prop: 'textContentAlignment',
    variants: {
      [TIMELINE_TEXT_ALIGNMENT.CENTER]: {
        alignSelf: 'center',
      },
    },
  }),
);

themeStyles.addBaseStyles('Play')(
  css({
    border: 0,
    background: 'transparent',
    boxSizing: 'border-box',
    width: 0,
    height: '12px',
    borderColor: 'transparent transparent transparent white',
    transition: '100ms all ease',
    cursor: 'pointer',
    borderStyle: 'solid',
    borderWidth: '8px 0 8px 12px',
  }),
);

export default themeStyles;
